import { Loader2, UserPen } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"

import { Button } from "@components/ui/button"
import { Input } from "@components/ui/input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@components/ui/form"
import { VerificationChecklist } from "@components/VerificationChecklist"
import { AccountContactPreferencesForm } from "@components/AccountNew/AccountContactPreferencesForm/AccountContactPreferencesForm"
import { useAccountOfferFunnel } from "@components/AccountNew/OfferFunnel/utils/useAccountOfferFunnel"
import { useSerializeOfferScript } from "@components/AccountNew/OfferFunnel/utils/useSerializeOfferScript"
import {
  isRetentionOffer,
  isWinbackOffer,
  type TRetentionFunnelOffer,
  type TWinbackFunnelOffer,
} from "@components/AccountNew/OfferFunnel/utils/offerTypeGuards"
import { useDrawer } from "@contexts/DrawerContext"
import {
  useAcceptWinbackOfferMutation,
  useGetWinbackQuery,
} from "@apis/winbacks/useWinbacks"
import { useAcceptRetentionOfferMutation } from "@apis/retention/useRetention"
import { accountKeys } from "@apis/accounts/queryKeys"
import { OFFER_TYPES } from "@apis/offers/types"
import type { TVerificationForm } from "@utils/verificationScriptsUtils"

export const Verification = () => {
  const {
    eligibleOfferType,
    setStep,
    account_id,
    selectedOffer,
    setSelectedOffer,
    tpvType,
    setConfirmedOrder,
  } = useAccountOfferFunnel()
  const { openDrawer } = useDrawer()

  const { script, market, account_number, prospect_guid } =
    useSerializeOfferScript()

  const queryClient = useQueryClient()
  const getWinbackQuery = useGetWinbackQuery(
    account_id,
    eligibleOfferType === OFFER_TYPES.Winback,
  )

  const acceptWinbackOfferMutation = useAcceptWinbackOfferMutation()
  const acceptRetentionOfferMutation = useAcceptRetentionOfferMutation()

  const form = useForm<TVerificationForm>({
    defaultValues: {
      account_number: account_number || "",
      verificationItem: script.reduce(
        (acc, _, index) => {
          acc[index.toString()] = false
          return acc
        },
        {} as Record<string, boolean>,
      ),
    },
    mode: "onChange",
  })

  const onSubmit = (values: TVerificationForm) => {
    if (
      eligibleOfferType === OFFER_TYPES.Winback &&
      isWinbackOffer(selectedOffer)
    ) {
      return handleSubmitWinback(values, selectedOffer)
    }

    if (
      eligibleOfferType === OFFER_TYPES.Retention &&
      isRetentionOffer(selectedOffer)
    ) {
      return handleSubmitRetention(selectedOffer)
    }

    // The selected offer does not belong to the account's current offer type, so
    // submitting it would send it through the wrong endpoint (see NEE-4061).
    toast.error(
      "This offer no longer matches the account's offer type. Please choose an offer again.",
      { duration: 10000 },
    )
    setSelectedOffer(null)
    setStep("chooseOffer")
  }

  const handleSubmitWinback = (
    values: TVerificationForm,
    offer: TWinbackFunnelOffer,
  ) => {
    acceptWinbackOfferMutation.mutate(
      {
        account_id,
        offer_code: offer.offer_code,
        request: {
          winback_id: getWinbackQuery.data?.winback_id as number,
          lock_from_dt: offer.lock_from_dt,
          lock_to_dt: offer.lock_to_dt,
          account_winback_status_history_id:
            offer.account_winback_status_history
              .account_winback_status_history_id,
          offered_dt: offer.account_winback_status_history.offered_dt,
          campaign_id: offer.campaign_id,
          tpv_type: tpvType as string,
          conversion_medium: "INSPIRE_CRM",
          account_number: values.account_number as string,
        },
      },
      {
        onSuccess: (res) => {
          setConfirmedOrder(res)
          toast.success("Successfully updated submitted Winback order!")
          setStep("confirmation")
          queryClient.invalidateQueries({
            queryKey: accountKeys.detail(account_id),
          })
        },
        onError: (error) => {
          toast.error(
            `There was a problem saving the winback offer. Please try again. ${error}`,
            { duration: 10000 },
          )
          setStep("initial")
        },
      },
    )
  }

  const handleSubmitRetention = (offer: TRetentionFunnelOffer) => {
    acceptRetentionOfferMutation.mutate(
      {
        account_id,
        offer_code: offer.offer_code,
        request: {
          lock_from_dt: offer.retention_details?.lock_from_dt as string,
          lock_to_dt: offer.retention_details?.lock_to_dt as string,
          offered_dt: offer.account_retention_status_history.offered_dt,
          campaign_id: offer.campaign_id,
          offer_group_guid: offer.offer_group_guid,
          tpv_type: tpvType as string,
          conversion_medium: "INSPIRE_CRM",
        },
      },
      {
        onSuccess: (res) => {
          setConfirmedOrder(res)
          toast.success("Successfully updated submitted Retention order!")
          setStep("confirmation")
        },
        onError: (error) => {
          toast.error(
            `There was a problem saving the retention offer. Please try again. ${error}`,
            { duration: 10000 },
          )
          setStep("initial")
        },
      },
    )
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <h2 className="tw:mb-0 tw:text-xl tw:font-semibold">Are You Sure?</h2>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="tw:flex tw:flex-col tw:gap-2"
        >
          {eligibleOfferType === OFFER_TYPES.Winback && (
            <FormField
              control={form.control}
              name="account_number"
              rules={{
                required: `${market?.account_num_synonym} is required.`,
                pattern: {
                  value: new RegExp(
                    market?.validation_rules.account_number_regexp || ".*",
                  ),
                  message: `Please enter a valid ${market?.account_num_synonym}. Example: ${market?.validation_rules.account_number_example}`,
                },
              }}
              render={({ field }) => (
                <FormItem className="tw:flex tw:flex-col tw:space-y-1">
                  <FormLabel htmlFor="account_number">
                    Verify {market?.account_num_synonym}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      id="account_number"
                      placeholder={`Enter ${market?.account_num_synonym}`}
                    />
                  </FormControl>
                  {form.formState.errors.account_number && (
                    <p className="tw:text-sm tw:text-destructive">
                      {form.formState.errors.account_number.message}
                    </p>
                  )}
                </FormItem>
              )}
            />
          )}

          <VerificationChecklist script={script} form={form} />

          <div className="tw:flex tw:justify-between">
            <Button
              variant="warning"
              onClick={() => setStep("offerDetail")}
              type="button"
            >
              Go Back
            </Button>

            <div className="tw:flex tw:gap-3">
              <Button
                type="button"
                onClick={() =>
                  openDrawer(
                    <AccountContactPreferencesForm
                      prospect_guid={prospect_guid}
                    />,
                    "Update Contact Preferences",
                  )
                }
                variant="outline"
              >
                <UserPen />
              </Button>

              <Button
                type="submit"
                disabled={
                  !form.formState.isValid ||
                  acceptWinbackOfferMutation.isPending ||
                  acceptRetentionOfferMutation.isPending
                }
              >
                {acceptWinbackOfferMutation.isPending ||
                acceptRetentionOfferMutation.isPending ? (
                  <>
                    Submitting Order
                    <Loader2 className="tw:animate-spin" />
                  </>
                ) : (
                  "Accept Offer"
                )}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}
